// API service for communicating with serverless backend

// Get the API base URL
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

// Generic API request function
const makeRequest = async (endpoint, data = {}) => {
  try {
    const url = API_BASE_URL ? `${API_BASE_URL}${endpoint}` : `/api${endpoint}`;
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ 
        error: `HTTP ${response.status}: ${response.statusText}` 
      }));
      throw new Error(errorData.error || errorData.message || `Request failed with status ${response.status}`);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error(`API request failed for ${endpoint}:`, error);
    throw error;
  }
};

// Create payment intent
export const createPaymentIntent = async (sessionId, packageId, amount, customerInfo) => {
  const data = {
    session_id: sessionId,
    package_id: packageId,
    amount: amount, // Amount should already be in cents from stripe config
    currency: 'usd',
    customer_info: {
      name: customerInfo.name,
      email: customerInfo.email
    }
  };

  return await makeRequest('/create-payment-intent', data);
};

// Confirm payment after successful Stripe payment
export const confirmPayment = async (sessionId, paymentIntentId, formData = {}) => {
  const data = {
    session_id: sessionId,
    payment_intent_id: paymentIntentId,
    form_data: formData
  };

  return await makeRequest('/confirm-payment', data);
};

// Save form step data (optional - for multi-step form persistence)
export const saveFormStep = async (sessionId, step, stepData) => {
  // This could be implemented as another serverless function
  // For now, we'll store in localStorage as backup
  try {
    const storageKey = `nc_form_${sessionId}`;
    const existingData = JSON.parse(localStorage.getItem(storageKey) || '{}');
    
    const updatedData = {
      ...existingData,
      [`step_${step}`]: stepData,
      last_updated: new Date().toISOString()
    };
    
    localStorage.setItem(storageKey, JSON.stringify(updatedData));
    
    return {
      success: true,
      step: step,
      saved_at: new Date().toISOString()
    };
  } catch (error) {
    console.error('Failed to save form step:', error);
    throw error;
  }
};

// Get saved form data (optional)
export const getSavedFormData = (sessionId) => {
  try {
    const storageKey = `nc_form_${sessionId}`;
    const savedData = localStorage.getItem(storageKey);
    return savedData ? JSON.parse(savedData) : null;
  } catch (error) {
    console.error('Failed to retrieve saved form data:', error);
    return null;
  }
};

// Clear saved form data (optional)
export const clearSavedFormData = (sessionId) => {
  try {
    const storageKey = `nc_form_${sessionId}`;
    localStorage.removeItem(storageKey);
    return true;
  } catch (error) {
    console.error('Failed to clear saved form data:', error);
    return false;
  }
};

// Error handling utility
export const handleApiError = (error) => {
  console.error('API Error:', error);
  
  // Map common Stripe error types
  const errorMessages = {
    'card_declined': 'Your card was declined. Please try a different payment method.',
    'expired_card': 'Your card has expired. Please use a different card.',
    'insufficient_funds': 'Insufficient funds. Please try a different payment method.',
    'invalid_cvc': 'Your card\'s security code is incorrect.',
    'processing_error': 'An error occurred while processing your card. Please try again.',
    'generic_decline': 'Your card was declined. Please try a different payment method.',
    'payment_intent_authentication_failure': 'Payment authentication failed. Please try again.',
    'network_error': 'Network error. Please check your connection and try again.'
  };
  
  if (error.code && errorMessages[error.code]) {
    return errorMessages[error.code];
  }
  
  if (error.message) {
    return error.message;
  }
  
  return 'An unexpected error occurred. Please try again.';
};

// Validate API response
export const validateApiResponse = (response) => {
  if (!response) {
    throw new Error('No response received from server');
  }
  
  if (response.error) {
    throw new Error(response.error);
  }
  
  return response;
};
